import dayjs from "dayjs"

const FMT = 'YYYY-MM-DD'

export type DateRange = [string | null, string | null]

export interface DateParams {
  date: string
  startDate: string
  endDate: string
}

export const DATE_PRESET_OPTIONS = [
  { value: 'today', label: '本日' },
  { value: 'week', label: '過去7日間' },
  { value: 'this_month', label: '今月' },
  { value: 'last_month', label: '先月' },
  { value: '6month', label: '半年' },
  { value: 'year', label: '1年' },
  { value: 'custom', label: 'カスタム' },
] as const

export const NO_DATE_OPTION = { value: '', label: '指定なし' } as const

// CV一覧の初期プリセット。検索フォームの initialValues と初回フェッチ条件で共有する
export const CV_LIST_INITIAL_PRESET = 'this_month'

// カレンダー月の境界を持つプリセットはサーバが解釈できないため custom + from/to に変換して送る
// 定数ではなく関数で保持しているのは、画面を開いたまま月を跨いでも呼び出し時点の月で再計算させるため
const PRESET_RANGES: Record<string, () => [string, string]> = {
  this_month: () => [
    dayjs().startOf('month').format(FMT),
    dayjs().format(FMT),
  ],
  last_month: () => [
    dayjs().subtract(1, 'month').startOf('month').format(FMT),
    dayjs().subtract(1, 'month').endOf('month').format(FMT),
  ],
}

export const getPresetRange = (date: string): [string, string] | null => {
  const range = PRESET_RANGES[date]
  return range ? range() : null
}

export const resolveDateParams = (date: string, dateRange: DateRange): DateParams => {
  const presetRange = getPresetRange(date)
  if (presetRange) {
    return { date: 'custom', startDate: presetRange[0], endDate: presetRange[1] }
  }

  if (date !== 'custom') {
    return { date, startDate: '', endDate: '' }
  }

  let [start, end] = dateRange
  if (start && end && start > end) {
    // YYYY-MM-DD は辞書順=日付順なので文字列比較でよい
    ;[start, end] = [end, start]
  }
  return { date: 'custom', startDate: start ?? '', endDate: end ?? '' }
}

// from/to を明示的に受け取るAPI向けに、相対プリセットもクライアント側で解決する
// 日付境界は cv.rb の limit_date_sql と揃える（DATE_SUB の基準日は範囲に含めない）
// 定数ではなく関数で保持しているのは、画面を開いたまま日付を跨いでも呼び出し時点で再計算させるため
const RELATIVE_PRESET_RANGES: Record<string, () => [string, string]> = {
  today: () => [dayjs().format(FMT), dayjs().format(FMT)],
  week: () => [dayjs().subtract(7, 'day').add(1, 'day').format(FMT), dayjs().format(FMT)],
  '6month': () => [dayjs().subtract(6, 'month').add(1, 'day').format(FMT), dayjs().format(FMT)],
  year: () => [dayjs().subtract(1, 'year').add(1, 'day').format(FMT), dayjs().format(FMT)],
}

// 全プリセットを [from, to] に解決する。custom で片方でも未入力なら null を返す
// （CRMスコアリング分析とサマリーで使用。サーバが date 文字列を解釈する画面は resolveDateParams を使う）
export const resolvePresetDateRange = (
  date: string,
  dateRange: DateRange,
): [string, string] | null => {
  const calendarRange = getPresetRange(date)
  if (calendarRange) return calendarRange

  const relativeRange = RELATIVE_PRESET_RANGES[date]
  if (relativeRange) return relativeRange()

  if (date !== 'custom') return null

  let [start, end] = dateRange
  if (!start || !end) return null
  if (start > end) {
    // YYYY-MM-DD は辞書順=日付順なので文字列比較でよい
    ;[start, end] = [end, start]
  }
  return [start, end]
}
